// StatusPage.tsx

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@mantine/core";
import ConfirmationImage from "../../../../../assets/Confirmation-Page.png";
import WelcomeImage from "../../../../../assets/Taxorganizer-Welcome.png";
import "./StatusPage.scss";
import { IconShieldLock } from "@tabler/icons-react";
import { useTaxOrganizerStore } from "../../../../../utils/stores/taxOrganizerStore";
import confetti from "canvas-confetti";
import { AppRoutes } from "@/constants.ts";
import { useAuthStore } from "@utils/stores/authStore";
import { openNewTab } from "@/utils/browserUtils";

/**
 * @function handleNextPage
 * @description Redirects the user to the next page in the organizer
 * @returns {void}
 */
const handleNextPage = (): void => {
  const goToNextPage = useTaxOrganizerStore.getState().goToNextPage;
  goToNextPage();
};

/**
 * @function showPrivacyPolicy
 * @description Shows Smartwiz Privacy Policy in a new tab
 * @returns {void}
 */
const showPrivacyPolicy = (): void => {
  openNewTab("https://smartwiz.io/privacy-policy");
};

/**
 * @function logoutUser
 * @description Logout user
 * @returns {void}
 */
const logoutUser = (): void => {
  window.location.href = AppRoutes.logout;
};

/**
 * @function handleConfetti
 * @description Throws a confetti on submission
 * @returns {void}
 */
const handleConfetti = () => {
  confetti({
    particleCount: 550,
    spread: 400,
    startVelocity: 40,
    origin: { x: 0.6, y: 0 },
    gravity: 1,
  });
};

interface StatusPageProps {
  isConfirmation: boolean;
}

/**
 * @function StatusPage
 * @description Renders either a welcome page or a confirmation page based on the isConfirmation prop.
 * @param {StatusPageProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
const StatusPage: React.FC<StatusPageProps> = ({
  isConfirmation,
}): JSX.Element => {
  const navigate = useNavigate();

  // Get client name from authStore
  const clientName = useAuthStore((state) => state.user?.firstName ?? "Client");

  /**
   * @function redirectToHome
   * @description Redirects the user to the client home page using React Router navigation.
   * @returns {void}
   */
  const redirectToHome = (): void => {
    // Reset organizer store to clear all form data before redirecting
    useTaxOrganizerStore.getState().resetStore();
    navigate(AppRoutes.clientHome);
  };

  // Only show confetti when user just completed the organizer, not when loading an already-completed one
  useEffect(() => {
    if (isConfirmation) {
      // Check if this is a fresh completion (status just changed to complete in this session)
      const confettiShownKey = `confetti-shown-${window.location.pathname}`;
      const hasShownConfetti = sessionStorage.getItem(confettiShownKey);

      // Also check if user just navigated here (not a page load/refresh)
      const isJustCompleted = sessionStorage.getItem(
        "organizer-just-completed"
      );

      if (!hasShownConfetti && isJustCompleted === "true") {
        handleConfetti();
        sessionStorage.setItem(confettiShownKey, "true");
        sessionStorage.removeItem("organizer-just-completed"); // Clear the flag
      }
    }
  }, [isConfirmation]);

  return (
    <div className="status-container">
      <div className="status-header">
        <img
          src={isConfirmation ? ConfirmationImage : WelcomeImage}
          alt={isConfirmation ? "Confirmation" : "Welcome"}
          className="status-image"
        />
        <h1>
          {isConfirmation
            ? "Congratulations!"
            : `Hi, ${clientName}! Let's get started!`}
        </h1>
        <p>
          {isConfirmation
            ? "You've successfully added your tax information."
            : ``}
        </p>
      </div>

      <div className="status-body">
        <p>
          {isConfirmation ? (
            "Your tax professional is now reviewing your submission and preparing your return. You can expect to hear back within a few days."
          ) : (
            <>
              We need to collect a few details so that we can make filing your
              taxes fast and easy. All of the information you share is protected
              and confidential. For more information, you can always review our{" "}
              <button
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  padding: 0,
                }}
                onClick={showPrivacyPolicy}
              >
                <strong className="status-strong">privacy policy</strong>
              </button>
              {"."}
            </>
          )}
        </p>

        <p>
          {isConfirmation ? (
            <>
              In the meantime, feel free to log in and check the status of your
              return. But for now, you can <br />{" "}
              <button
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  padding: 0,
                }}
                onClick={logoutUser}
              >
                <strong className="status-strong">log off and relax</strong>
              </button>
              {"."}
            </>
          ) : (
            ""
          )}
        </p>
      </div>

      <Button
        size="lg"
        radius="md"
        className="status-button"
        onClick={isConfirmation ? redirectToHome : handleNextPage}
      >
        {isConfirmation ? "Go to My Dashboard" : "Let's Get Started"}
      </Button>
      {isConfirmation ? null : (
        <div className="status-footer">
          <IconShieldLock className="status-icon" />
          <p>
            Smartwiz is committed to securing your data.{" "}
            <button
              style={{
                backgroundColor: "transparent",
                border: "none",
              }}
              onClick={showPrivacyPolicy}
            >
              <strong className="status-strong">Learn More</strong>
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default StatusPage;
